import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import practiceClient from '../client/PracticeClient';

/**
 * Методика колоды и картина колоды под ней.
 *
 * Метод и сводка приходят вместе, потому что порознь они врут: подпись «коробки» над
 * столбиками сроков — это неверная страница, а не мелкая рассинхронизация. Ответ на
 * запрос по прошлой колоде игнорируется — тот же приём, что в useDeckPracticeSummary:
 * при быстром переключении колод поздний ответ не должен перезаписать более свежий.
 *
 * `errorMessage` — уже переведённая строка, а не функция перевода: строка сравнивается
 * по значению и не перезапускает загрузку на каждом рендере.
 */
const useDeckLearningMethod = (deckId, practiceType, errorMessage) => {
    const [loaded, setLoaded] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSwitching, setIsSwitching] = useState(false);

    // Одно поколение на колоду и тип: и первая загрузка, и перечитывание после
    // переключения проверяют его перед записью. Смена колоды или размонтирование
    // поднимают поколение, и всё, что было в полёте, свой ответ не применяет. Раньше у
    // переключения был свой флаг отмены, который никто не поднимал.
    const generation = useRef(0);

    const load = useCallback(async () => {
        const started = generation.current;
        const stats = await practiceClient.getDeckLearningStats(deckId, practiceType);
        if (started === generation.current) {
            setLoaded({ deckId, practiceType, stats });
        }
    }, [deckId, practiceType]);

    useEffect(() => {
        const current = generation;
        current.current += 1;
        if (!deckId) {
            setIsLoading(false);
            return undefined;
        }
        const started = current.current;
        const isCurrent = () => started === current.current;
        setIsLoading(true);
        load()
            .catch((error) => {
                if (!isCurrent()) {
                    return;
                }
                console.error('Не удалось загрузить методику колоды:', error);
                message.error(errorMessage);
            })
            .finally(() => {
                if (isCurrent()) {
                    setIsLoading(false);
                }
            });
        return () => {
            current.current += 1;
        };
    }, [deckId, practiceType, errorMessage, load]);

    /**
     * Переключает методику и перечитывает картину. Сначала запись, потом чтение, а не
     * оптимистичное обновление: сервер вместе с выбором пересчитывает сроки, и число
     * в каждом столбике после переключения знает только он.
     */
    const switchTo = useCallback(async (learningMethod) => {
        if (!deckId) {
            return;
        }
        setIsSwitching(true);
        const started = generation.current;
        try {
            const applied = await practiceClient.setDeckLearningMethod(deckId, learningMethod);
            // Выбор уже записан и сроки уже пересчитаны. Показать после этого прежнюю
            // методику значило бы соврать: страница слала бы старую в запросе на
            // открытие практики, а расписание считалось бы новой. Корзины при этом
            // сбрасываются — они посчитаны для прежней политики, и оставить их под
            // новой подписью хуже, чем не показать вовсе.
            if (started === generation.current) {
                setLoaded((previous) => ({
                    deckId,
                    practiceType,
                    stats: {
                        ...previous?.stats,
                        learningMethod: applied?.learningMethod ?? learningMethod,
                        buckets: null,
                    },
                }));
            }
        } catch (error) {
            console.error('Не удалось переключить методику:', error);
            if (started === generation.current) {
                message.error(errorMessage);
            }
        }
        // Перечитывание — отдельно от переключения, и после ошибки тоже: сервер пишет
        // выбор первым, и ответ мог потеряться уже после записи. Без перечитывания
        // страница открывала бы практику под методикой, от которой база уже
        // отказалась. Сбой самого перечитывания сообщением не считается: то, что было
        // на экране, не хуже, а «не удалось переключить» после удавшегося переключения
        // было бы неправдой.
        try {
            await load();
        } catch (reloadError) {
            console.error('Не удалось перечитать методику колоды:', reloadError);
        } finally {
            if (started === generation.current) {
                setIsSwitching(false);
            }
        }
    }, [deckId, practiceType, errorMessage, load]);

    const stats = loaded && loaded.deckId === deckId && loaded.practiceType === practiceType
        ? loaded.stats
        : null;

    return {
        stats,
        learningMethod: stats?.learningMethod ?? null,
        isLoading,
        isSwitching,
        switchTo,
    };
};

export default useDeckLearningMethod;
